import { Component, OnDestroy, OnInit, PLATFORM_ID, computed, effect, inject, signal, untracked, viewChildren } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { LucideAngularModule, Phone, ChevronUp, UserPlus } from 'lucide-angular';
import { CallService } from '../../../core/services/call.service';
import { TranslationService } from '../../../core/services/translation.service';
import { ChatService, Colleague } from '../../../core/services/chat.service';
import { UserService } from '../../../core/services/user.service';
import { AppelEntrantComponent } from './appel-entrant.component';
import { AjouterParticipantComponent } from './ajouter-participant.component';
import { AppelTermineComponent } from './appel-termine.component';
import { EcranAppelActifComponent } from './ecran-appel-actif.component';
import { GrilleAppelGroupeComponent } from './grille-appel-groupe.component';
import { SortieAudioComponent } from './sortie-audio.component';
import { SalleAppel } from '../../../core/services/salle-appel.service';
import { PRESENCE_INITIALE, PresenceAppel, evaluerPresence } from '../../../core/services/regle-fin-appel';
import { degradeAvatar, initiale, urlPhoto } from './avatars-appel';
import { PhotoAppelComponent } from './photo-appel.component';
import { construireTuiles } from './tuiles-appel';

/**
 * MOB-010 — appel entrant / en cours / minimisé, montés UNE SEULE FOIS dans
 * `MainLayoutComponent` (comme `<app-cash-session-bar>`) — jamais dans une
 * route qui se démonte à la navigation, ce qui casserait justement la
 * persistance de l'appel pendant qu'on navigue ailleurs dans l'app.
 *
 * `<legba-call>` (Web Component Lit de `@legba-core/ui`) n'est chargé que
 * côté navigateur : Lit suppose `customElements`, absent en SSR.
 */
@Component({
    selector: 'app-call-overlay',
    standalone: true,
    imports: [
        CommonModule, LucideAngularModule, AppelEntrantComponent, AjouterParticipantComponent, AppelTermineComponent,
        EcranAppelActifComponent, GrilleAppelGroupeComponent, SortieAudioComponent, PhotoAppelComponent,
    ],
    templateUrl: './call-overlay.component.html',
})
export class CallOverlayComponent implements OnInit, OnDestroy {
    callService = inject(CallService);
    translationService = inject(TranslationService);
    private chatService = inject(ChatService);
    private userService = inject(UserService);
    private platformId = inject(PLATFORM_ID);
    t = this.translationService.t;

    private salle = inject(SalleAppel);

    readonly Phone = Phone;
    readonly ChevronUp = ChevronUp;
    readonly UserPlus = UserPlus;

    /** Nom de la personne connectee, pour l'ecran « Appel termine ». */
    readonly moi = computed(() => this.userService.profile().name);

    activeCall = this.callService.activeCall;
    lastEndedCall = this.callService.lastEndedCall;

    // ─── Ajouter un participant — feuille en bas d'écran ───
    readonly showAddParticipant = signal(false);
    readonly colleagueSearch = signal('');

    /**
     * `Colleague.status` vaut toujours `'offline'` en dur côté client
     * [chat.service.ts `fetchColleagues`] — aucune vraie présence n'existe
     * nulle part dans le produit. Volontairement AUCUN indicateur en
     * ligne/hors ligne ici, plutôt que d'en afficher un qui mentirait à
     * chaque fois.
     */
    private readonly filteredColleagues = computed(() => {
        const q = this.colleagueSearch().trim().toLowerCase();
        const all = this.chatService.colleagues();
        return q ? all.filter((c) => c.name.toLowerCase().includes(q)) : all;
    });

    /** Ceux déjà dans la conversation de cet appel (donc déjà atteignables) vs le reste de l'entreprise. */
    readonly colleaguesInConversation = computed(() => {
        const call = this.activeCall();
        if (!call) return [];
        const conv = this.chatService.conversations().find((c) => c.id === String(call.conversationId));
        const ids = new Set((conv?.participantIds ?? []).map(String));
        return this.filteredColleagues().filter((c) => ids.has(String(c.id)));
    });

    readonly otherColleagues = computed(() => {
        const call = this.activeCall();
        const conv = call ? this.chatService.conversations().find((c) => c.id === String(call.conversationId)) : undefined;
        const ids = new Set((conv?.participantIds ?? []).map(String));
        return this.filteredColleagues().filter((c) => !ids.has(String(c.id)));
    });

    // ─── Salle : participants, noms, son ───

    /**
     * Identite LiveKit = identifiant de l'utilisateur : le nom vient des collegues connus du poste.
     * Faute de correspondance, un seul interlocuteur distant est forcement celui de l'appel.
     */
    private nomDe(identity: string): string | undefined {
        const collegue = this.chatService.colleagues().find((c) => String(c.id) === identity);
        if (collegue) return collegue.name;
        const distants = this.salle.participants().filter((p) => !p.moi);
        return distants.length === 1 ? this.activeCall()?.peerName : undefined;
    }

    /** La photo d'un participant : celle de son profil de collegue, sinon (interlocuteur unique) celle de la conversation. */
    private photoDe(identity: string): string | undefined {
        const collegue = this.chatService.colleagues().find((c) => String(c.id) === identity);
        if (collegue) return urlPhoto(collegue.avatarUrl);
        const distants = this.salle.participants().filter((p) => !p.moi);
        return distants.length === 1 ? this.photoInterlocuteur() : undefined;
    }

    /** La photo de l'interlocuteur de l'appel : celle de la conversation, ou de son profil de collegue. */
    readonly photoInterlocuteur = computed(() => {
        const call = this.activeCall();
        if (!call) return undefined;
        const conv = this.chatService.conversations().find((c) => c.id === String(call.conversationId));
        const parCollegue = this.chatService.colleagues().find((c) => c.name === call.peerName);
        return urlPhoto(conv?.avatarUrl) ?? urlPhoto(parCollegue?.avatarUrl);
    });

    readonly tuiles = computed(() => {
        const c = this.t().calls;
        return construireTuiles(this.salle.participants(), (id) => this.nomDe(id), this.moi(), c.participant_unknown, c.you,
            (id) => this.photoDe(id), urlPhoto(this.userService.profile().avatar));
    });

    readonly tuilesDistantes = computed(() => this.tuiles().filter((p) => !p.moi && p.audioTrack));

    /** Photos de profil connues, par nom : collegues, soi-meme, et l'interlocuteur de l'appel qui vient de finir. */
    readonly photosParNom = computed(() => {
        const photos: Record<string, string> = {};
        for (const c of this.chatService.colleagues()) {
            const url = urlPhoto(c.avatarUrl);
            if (url) photos[c.name] = url;
        }
        const maPhoto = urlPhoto(this.userService.profile().avatar);
        if (maPhoto && this.moi().trim()) photos[this.moi().trim()] = maPhoto;
        const fini = this.lastEndedCall();
        if (fini) {
            const conv = this.chatService.conversations().find((c) => c.id === String(fini.conversationId));
            const urlConv = urlPhoto(conv?.avatarUrl);
            if (urlConv) photos[fini.peerName] = urlConv;
        }
        return photos;
    });

    readonly initialeDe = initiale;
    readonly degradeDe = degradeAvatar;

    /** Vrai quand le navigateur a refuse de jouer le son sans geste de l'utilisateur. */
    readonly sonBloque = signal(false);
    private readonly sorties = viewChildren(SortieAudioComponent);

    async activerSon() {
        await Promise.all(this.sorties().map((s) => s.reprendre()));
        this.sonBloque.set(false);
    }

    openAddParticipant() {
        this.colleagueSearch.set('');
        this.showAddParticipant.set(true);
    }

    closeAddParticipant() {
        this.showAddParticipant.set(false);
    }

    /** Invite une personne ; rend faux si le serveur a refusé, pour ne pas fermer la feuille sur un echec. */
    async inviteColleague(colleague: Colleague): Promise<boolean> {
        try {
            await this.callService.invite(colleague.id, colleague.name);
            return true;
        } catch {
            return false;
        }
    }

    /**
     * Invite ceux choisis dans la feuille, un par un (ordre d'affichage). Une invitation refusee n'empeche pas
     * les suivantes, et la feuille ne se referme que quand TOUTES ont abouti : elle reste ouverte pour reessayer.
     */
    async inviteColleagues(colleagues: Colleague[]) {
        let toutesInvitees = true;
        for (const colleague of colleagues) {
            toutesInvitees = (await this.inviteColleague(colleague)) && toutesInvitees;
        }
        if (toutesInvitees) this.closeAddParticipant();
    }

    // ─── Appel terminé — résumé après avoir raccroché ───

    readonly endedCallDuration = computed(() => {
        const ended = this.lastEndedCall();
        if (!ended) return '00:00';
        const secs = Math.max(0, Math.floor((ended.endedAt - ended.startedAt) / 1000));
        const m = Math.floor(secs / 60).toString().padStart(2, '0');
        const s = (secs % 60).toString().padStart(2, '0');
        return `${m}:${s}`;
    });

    dismissEnded() {
        this.callService.dismissEndedCall();
    }

    async recall() {
        const ended = this.lastEndedCall();
        if (!ended) return;
        this.callService.dismissEndedCall();
        await this.callService.initiateCall(ended.conversationId, ended.type, ended.peerName);
    }

    // Rien n'alertait la caissière d'un appel entrant en dehors de l'écran
    // lui-même (pas de son, pas de notification) — un changement d'état
    // purement visuel, invisible si on ne regarde pas l'écran au bon moment.
    private ringAudioContext: AudioContext | null = null;
    private ringIntervalId: ReturnType<typeof setInterval> | null = null;
    private wasRinging = false;

    // Durée réelle de l'appel actif — pas un habillage : mesurée depuis le
    // premier instant en phase `active` de CE call précis (callId), pas
    // depuis le montage du composant (persistant, monté une seule fois).
    readonly elapsed = signal('00:00');
    private elapsedStartedAt: number | null = null;
    private elapsedIntervalId: ReturnType<typeof setInterval> | null = null;
    private elapsedForCallId: number | null = null;

    constructor() {
        effect(() => {
            const ringing = this.activeCall()?.phase === 'ringing';
            if (ringing && !this.wasRinging) this.startRinging();
            if (!ringing && this.wasRinging) this.stopRinging();
            this.wasRinging = ringing;
        });

        effect(() => {
            const call = this.activeCall();
            const inCall = call?.phase === 'active' || call?.phase === 'minimized';
            if (inCall && this.elapsedForCallId !== call.callId) {
                this.elapsedForCallId = call.callId;
                this.startElapsedTimer();
            } else if (!inCall && this.elapsedForCallId !== null) {
                this.elapsedForCallId = null;
                this.stopElapsedTimer();
            }
        });

        // La salle suit l'appel : elle s'ouvre quand le jeton signé est prêt, reste ouverte pendant
        // la minimisation, et se ferme quand l'appel disparaît (raccroché, refusé, coupé par l'autre).
        effect(() => {
            const call = this.activeCall();
            const enAppel = call && (call.phase === 'active' || call.phase === 'minimized');
            if (enAppel && call.callAuthUrl) {
                void this.salle.rejoindre({
                    roomName: call.roomName,
                    authUrl: call.callAuthUrl,
                    camera: call.type === 'video' && !call.audioSeul,
                });
            } else if (!call || call.phase === 'ringing') {
                void this.salle.quitter();
                this.sonBloque.set(false);
            }
        });

        effect(() => this.verifierPresence());
    }

    // LA REGLE DE FIN : un appel dont il ne reste que moi se ferme tout seul (voir regle-fin-appel.ts). A deux,
    // l'autre part -> je me ferme ; a trois ou plus, l'un part -> l'appel reste actif. Lue sur la salle, donc
    // indifferente a l'etat de l'API : si le serveur est en panne, les deux ecrans se ferment quand meme.
    private presence: PresenceAppel = PRESENCE_INITIALE;
    private presencePourAppel: number | null = null;

    private verifierPresence(): void {
        const call = this.activeCall();
        const enAppel = !!call && (call.phase === 'active' || call.phase === 'minimized');
        if (!enAppel) {
            this.presence = PRESENCE_INITIALE;
            this.presencePourAppel = null;
            return;
        }
        if (this.presencePourAppel !== call.callId) {
            this.presence = PRESENCE_INITIALE;
            this.presencePourAppel = call.callId;
        }
        // Seule une salle CONNECTEE dit qui est la : en connexion, en reconnexion ou coupee (c'est MA liaison
        // qui est en cause, pas celle des autres), le nombre de participants n'est pas fiable.
        if (this.salle.etat() !== 'connectee') return;
        const distants = this.salle.participants().filter((p) => !p.moi).length;
        this.presence = evaluerPresence(this.presence, distants);
        if (this.presence.fermer) {
            this.presence = PRESENCE_INITIALE;
            untracked(() => void this.callService.endCall());
        }
    }

    async ngOnInit() {
        if (isPlatformBrowser(this.platformId)) {
            this.callService.startPolling();
        }
    }

    ngOnDestroy() {
        this.stopRinging();
        this.stopElapsedTimer();
    }

    /** Deux tonalités alternées, en boucle — synthétisées, pas un fichier son (aucune licence à porter). */
    private startRinging() {
        if (!isPlatformBrowser(this.platformId)) return;
        this.notifyIncoming();

        const AudioCtx = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
        if (!AudioCtx) return;
        this.ringAudioContext = new AudioCtx();

        const ringOnce = () => {
            const ctx = this.ringAudioContext;
            if (!ctx) return;
            [440, 480].forEach((freq, i) => {
                const osc = ctx.createOscillator();
                const gain = ctx.createGain();
                osc.frequency.value = freq;
                gain.gain.value = 0.15;
                osc.connect(gain).connect(ctx.destination);
                const start = ctx.currentTime + i * 0.4;
                osc.start(start);
                osc.stop(start + 0.35);
            });
        };
        ringOnce();
        this.ringIntervalId = setInterval(ringOnce, 3000);
    }

    private startElapsedTimer() {
        if (!isPlatformBrowser(this.platformId)) return;
        this.elapsedStartedAt = Date.now();
        this.elapsed.set('00:00');
        this.elapsedIntervalId = setInterval(() => {
            const secs = Math.floor((Date.now() - (this.elapsedStartedAt ?? Date.now())) / 1000);
            const m = Math.floor(secs / 60).toString().padStart(2, '0');
            const s = (secs % 60).toString().padStart(2, '0');
            this.elapsed.set(`${m}:${s}`);
        }, 1000);
    }

    private stopElapsedTimer() {
        if (this.elapsedIntervalId !== null) {
            clearInterval(this.elapsedIntervalId);
            this.elapsedIntervalId = null;
        }
        this.elapsedStartedAt = null;
        this.elapsed.set('00:00');
    }

    private stopRinging() {
        if (this.ringIntervalId !== null) {
            clearInterval(this.ringIntervalId);
            this.ringIntervalId = null;
        }
        if (this.ringAudioContext) {
            void this.ringAudioContext.close();
            this.ringAudioContext = null;
        }
    }

    /**
     * Capte l'attention même si l'onglet n'est pas au premier plan — la
     * sonnerie seule ne suffit pas si le son est coupé. La `Notification`
     * simple (sans service worker) n'a pas de bouton Répondre/Refuser
     * fonctionnel : cliquer dessus doit au moins ramener l'onglet au premier
     * plan, sur le véritable écran d'appel — sinon la caissière clique sur la
     * notification en pensant y répondre, et rien ne se passe.
     */
    private notifyIncoming() {
        if (typeof Notification === 'undefined') return;
        const call = this.activeCall();
        if (!call) return;
        const title = this.t().calls?.incoming_audio || 'Incoming call';
        const show = () => {
            const n = new Notification(title, { body: call.peerName });
            n.onclick = () => {
                window.focus();
                n.close();
            };
        };
        if (Notification.permission === 'granted') {
            show();
        } else if (Notification.permission !== 'denied') {
            void Notification.requestPermission().then((p) => {
                if (p === 'granted') show();
            });
        }
    }

    answer(audioSeul = false) {
        void this.callService.answer({ audioSeul });
    }

    decline() {
        void this.callService.decline();
    }

    endCall() {
        void this.callService.endCall();
    }

    minimize() {
        this.callService.minimize();
    }

    expand() {
        this.callService.expand();
    }
}
